import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { Box, IconButton, Tooltip, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, TextField, Menu, MenuItem, ListItemIcon, ListItemText, InputBase, TableSortLabel, InputAdornment, Select, Chip, Backdrop, Paper, Typography, Divider, Button } from '@mui/material';
import { MoreVert, TableRows, TableChart, Search, UnfoldMore, Close, DragIndicator } from '@mui/icons-material';
import { useEstimateData } from '../../contexts/EstimateDataContext';
import { useAuth } from '../../contexts/AuthContext';
import { getSectionForNr, TesSection, findBestSection } from '../../utils/sectionUtils';
import { compareSectionNr } from '../../utils/sectionUtils';
import { updateRms } from '../../services/estimateService';
import api from '../../services/api';
import ExportToEasyEstimateDialog from './ExportToEasyEstimateDialog';

// Assume EstimatePosition and child types from existing code
interface EstimatePosition {
  id: number;
  nr: string;
  basis?: string;
  description: string;
  quantity: number;
  unit: string;
  positionType: string;
  children?: Array<{
    id: number;
    type: string;
    description: string;
    unit: string;
    norm: number;
    expenses: number;
    rmsPrice: number;
  }>;
}

interface UpdatePositionPayload {
  id: number;
  field:
    | 'description'
    | 'quantity'
    | 'unit'
    | 'unitPrice'
    | 'children'
    | 'norm'
    | 'expenses'
    | 'rmsPrice';
  value: any; // string | number | children[]
}

export interface EstimatePrecision {
  unitPrice: number;
  rmsPrice: number;
  quantity: number;
  expenses: number;
  norm: number;
}

const DEFAULT_PRECISION: EstimatePrecision = {
  unitPrice: 2,
  rmsPrice: 3,
  quantity: 3,
  expenses: 4,
  norm: 6,
};

interface DetailedEstimateSheetProps {
  positions: EstimatePosition[];
  estimateId: number;
  markups: { kp: number; kz: number; z: number };
  precision?: EstimatePrecision;
  onAddDivision: () => void;
  onAddPosition: () => void;
  onAddLabor: () => void;
  onAddEquipment: () => void;
  onAddMaterial: () => void;
  onUpdatePosition: (payload: UpdatePositionPayload) => void;
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  selectedRowId: string;
  onSelectRow: (rowId: string) => void;
}

interface SheetRowProps {
  selected: boolean;
}

// Funkcja formatująca liczby z oddzielaniem tysięcy i przecinkiem jako separatorem dziesiętnym
const formatNumber = (value: number | string, decimals: number = 2): string => {
  // Konwertujemy wartość na liczbę, jeśli jest stringiem
  const numValue = typeof value === 'string' ? parseFloat(value.replace(',', '.')) : value;
  
  // Sprawdzamy, czy wartość jest poprawną liczbą
  if (!isFinite(numValue) || isNaN(numValue)) {
    return '0,' + '0'.repeat(decimals);
  }
  
  // Intl.NumberFormat używa niełamiącej spacji (\u00A0) jako separatora tysięcy
  // Zamieniamy ją na zwykłą spację i upewniamy się, że wszystkie spacje są zwykłymi spacjami
  const formatted = new Intl.NumberFormat('pl-PL', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    useGrouping: true // Wymusza użycie separatora tysięcy
  }).format(numValue);
  
  // Zamienia niełamiącą spację (\u00A0) i wszystkie białe znaki na zwykłą spację
  return formatted.replace(/\u00A0/g, ' ').replace(/[\s\u2009]/g, ' ');
};

const DetailedEstimateSheet: React.FC<DetailedEstimateSheetProps> = ({
  positions,
  estimateId,
  markups,
  precision: precisionProp,
  onAddDivision,
  onAddPosition,
  onAddLabor,
  onAddEquipment,
  onAddMaterial,
  onUpdatePosition,
  undo,
  redo,
  canUndo,
  canRedo,
  selectedRowId,
  onSelectRow
}) => {
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [search, setSearch] = useState('');
  const [sectionFilter, setSectionFilter] = useState<string>('all');
  const [groupView, setGroupView] = useState<boolean>(true); // true = widok z działami
  const [sheetGenerating, setSheetGenerating] = useState(false);
  const [exportEeOpen, setExportEeOpen] = useState(false);
  const { data } = useEstimateData();

  const handleGenerateGoogleSheet = async () => {
    setSheetGenerating(true);
    try {
      const { data: res } = await api.post('/sheets/generate', data);
      if (res?.url) {
        window.open(res.url, '_blank');
      } else {
        alert('Wygenerowano arkusz, ale nie otrzymano linku.');
      }
    } catch (err: any) {
      console.error(err);
      alert(err?.response?.data?.message || 'Błąd podczas generowania arkusza Google Sheets.');
    } finally {
      setSheetGenerating(false);
    }
  };
  const { user } = useAuth();
  const precision = precisionProp ?? DEFAULT_PRECISION;
  const tesRows = (data?.tesRows || []) as TesSection[];

  // Zarządzanie kolumnami - widoczność
  const ALL_COLUMNS = ['nr', 'type', 'description', 'quantity', 'unit', 'norm', 'expenses', 'rmsPrice', 'unitPrice', 'value', 'actions'];
  const DEFAULT_VIS: Record<string, boolean> = useMemo(() => ({
    nr: true,
    type: true,
    description: true,
    quantity: true,
    unit: true,
    norm: true,
    expenses: true,
    rmsPrice: true,
    unitPrice: true,
    value: true,
    actions: true
  }), []);
  const VISIBLE_KEY = useMemo(() => `detailedEstimate.columns.visible:${user?.user_id || 'anon'}`, [user?.user_id]);
  const [columnsVisible, setColumnsVisible] = useState<Record<string, boolean>>(() => {
    try {
      const raw = localStorage.getItem(VISIBLE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        return { ...DEFAULT_VIS, ...parsed };
      }
    } catch {}
    return { ...DEFAULT_VIS };
  });
  const colV = useCallback((key: string) => columnsVisible[key] !== false, [columnsVisible]);

  // Szerokości kolumn
  const defaultColumnWidths = useMemo<Record<string, number>>(() => ({
    nr: 70,
    type: 40,
    description: 340,
    quantity: 110,
    unit: 70,
    norm: 90,
    expenses: 100,
    rmsPrice: 90,
    unitPrice: 110,
    value: 120,
    actions: 80 // Zwiększona szerokość dla ikony statusu + menu trzy kropki
  }), []);
  const WIDTHS_KEY = useMemo(() => `detailedEstimate.columns.widths:${user?.user_id || 'anon'}`, [user?.user_id]);
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(() => {
    try {
      const raw = localStorage.getItem(WIDTHS_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        return { ...defaultColumnWidths, ...parsed };
      }
    } catch {}
    return { ...defaultColumnWidths };
  });
  const getColWidthPx = useCallback((key: string): string => {
    const def = defaultColumnWidths[key] ?? 40;
    const cur = columnWidths[key];
    const width = typeof cur === 'number' ? Math.max(cur, def) : def;
    return `${width}px`;
  }, [columnWidths, defaultColumnWidths]);

  // Panel kolumn
  const [colsAnchorEl, setColsAnchorEl] = useState<null | HTMLElement>(null);
  const colsOpen = Boolean(colsAnchorEl);
  const [draftColumnsVisible, setDraftColumnsVisible] = useState<Record<string, boolean> | null>(null);
  const [draftWidths, setDraftWidths] = useState<Record<string, number>>({});
  const [colResizeMode, setColResizeMode] = useState<boolean>(false);
  const [dragCol, setDragCol] = useState<{ key: string; startX: number; startW: number } | null>(null);

  // Obsługa przeciągania do zmiany szerokości kolumn
  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!dragCol) return;
      e.preventDefault();
      const dx = e.clientX - dragCol.startX;
      const newW = Math.max(24, dragCol.startW + dx);
      setColumnWidths(prev => ({ ...prev, [dragCol.key]: newW }));
    };
    const onUp = (e: MouseEvent) => {
      e.preventDefault();
      setDragCol(null);
      try {
        const payload = JSON.stringify(columnWidths);
        localStorage.setItem(WIDTHS_KEY, payload);
      } catch {}
    };
    if (dragCol) {
      try { document.body.style.cursor = 'col-resize'; } catch {}
      window.addEventListener('mousemove', onMove, { passive: false } as any);
      window.addEventListener('mouseup', onUp as any);
    }
    return () => {
      window.removeEventListener('mousemove', onMove as any);
      window.removeEventListener('mouseup', onUp as any);
      try { document.body.style.cursor = ''; } catch {}
    };
  }, [dragCol, columnWidths, WIDTHS_KEY]);

  // Zapisz szerokości kolumn
  useEffect(() => {
    if (!dragCol) {
      try {
        const payload = JSON.stringify(columnWidths);
        localStorage.setItem(WIDTHS_KEY, payload);
      } catch {}
    }
  }, [columnWidths, WIDTHS_KEY, dragCol]);

  // Zapisz widoczność kolumn
  useEffect(() => {
    try {
      localStorage.setItem(VISIBLE_KEY, JSON.stringify(columnsVisible));
    } catch {}
  }, [VISIBLE_KEY, columnsVisible]);

  // ESC zamyka okno "Dostosuj kolumny" i wyłącza tryb resize
  useEffect(() => {
    if (!colsOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setDragCol(null);
        setColResizeMode(false);
        setColsAnchorEl(null);
        setDraftColumnsVisible(null);
        setDraftWidths({});
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [colsOpen]);

  // Refs do pola wyszukiwania i selektora filtrowania
  const searchInputRef = React.useRef<HTMLInputElement>(null);
  const filterSelectRef = React.useRef<HTMLInputElement>(null);
  // Ref do kontenera tabeli – używany w nawigacji klawiaturą
  const tableContainerRef = React.useRef<HTMLDivElement>(null);

  // focus on mount
  React.useEffect(()=>{
    tableContainerRef.current?.focus();
  },[]);
  type CellField = 'description'|'quantity'|'unit'|'unitPrice'|'norm'|'expenses'|'rmsPrice';
  const [selectedCell, setSelectedCell] = useState<{posId:number; expenseId?:number; field:CellField} | null>(null);
  const [editingCell, setEditingCell] = useState<{posId:number; expenseId?:number; field:CellField; value:string} | null>(null);

  // Stan edytowanej aktualnie normy RMS
  const [editingRms, setEditingRms] = useState<{
    positionId:number;
    expenseId:number;
    value:string;
  }|null>(null);

  const editableFields = (p:EstimatePosition): Array<'description'|'quantity'|'unit'|'unitPrice'> => {
    return p.positionType==='detailed' ? ['description','quantity','unit'] : ['description','quantity','unit','unitPrice'];
  };

  // Kolejność przechodzenia klawiszem → w nakładach RMS (bez kolumny „Nakłady” – jest tylko do odczytu)
  const editableFieldsExpense: CellField[] = ['description','unit','norm','rmsPrice'];

  const moveCell = (pos:EstimatePosition, dir:number) => {
    if(!selectedCell) return;
    const fields = selectedCell.expenseId ? editableFieldsExpense : editableFields(pos);
    const idx = fields.indexOf(selectedCell.field as any);
    const newIdx = idx + dir;
    if(newIdx>=0 && newIdx<fields.length){
      setSelectedCell({posId:pos.id, expenseId:selectedCell.expenseId, field: fields[newIdx] as CellField});
    }
  };

  const handleCellClick = (posId:number, expenseId:number, field:CellField, value:string|number)=>{
    setSelectedCell({posId, expenseId, field});
  };

  // Stan statusów pozycji (domyślnie "sprawdzić") - tylko dla wierszy głównych pozycji
  const [positionStatuses, setPositionStatuses] = useState<Record<string, 'approved' | 'check' | 'rejected'>>(() => {
    const statuses: Record<string, 'approved' | 'check' | 'rejected'> = {};
    positions.forEach(pos => {
      statuses[`pos-${pos.id}`] = 'check';
      // Nie dodajemy statusów dla wierszy składowych (exp)
    });
    return statuses;
  });

  // Aktualizuj statusy gdy pozycje się zmieniają - tylko dla wierszy głównych
  useEffect(() => {
    setPositionStatuses(prev => {
      const updated = { ...prev };
      positions.forEach(pos => {
        if (!updated[`pos-${pos.id}`]) {
          updated[`pos-${pos.id}`] = 'check';
        }
        // Nie dodajemy statusów dla wierszy składowych (exp)
      });
      return updated;
    });
  }, [positions]);

  // Funkcja renderująca ikonę statusu
  const getStatusIcon = (status: 'approved' | 'check' | 'rejected') => {
    switch (status) {
      case 'approved':
        return <Box component="img" src="/assets/icons/zatwierdzam.svg" alt="Zatwierdzone" sx={{ width: 18, height: 18 }} />;
      case 'rejected':
        return <Box component="img" src="/assets/icons/odrzucam.svg" alt="Odrzucone" sx={{ width: 18, height: 18 }} />;
      case 'check':
      default:
        return <Box component="img" src="/assets/icons/check.svg" alt="Sprawdzić" sx={{ width: 18, height: 18 }} />;
    }
  };

  // menu dla akcji edycja/usuń
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuRowId, setMenuRowId] = useState<string | null>(null);
  const [selectedMenuItemIndex, setSelectedMenuItemIndex] = useState<number>(0);
  const menuOpen = Boolean(menuAnchorEl);

  const handleMenuOpen = (e: React.MouseEvent<HTMLButtonElement>, rowId: string) => {
    setMenuAnchorEl(e.currentTarget);
    setMenuRowId(rowId);
    setSelectedMenuItemIndex(0); // Resetuj zaznaczenie przy otwieraniu menu
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setMenuRowId(null);
    setSelectedMenuItemIndex(0);
  };

  const handleEditRow = () => {
    console.log('Edytuj', menuRowId);
    handleMenuClose();
  };

  const handleDeleteRow = () => {
    console.log('Usuń', menuRowId);
    handleMenuClose();
  };

  const handleStatusChange = (status: 'approved' | 'check' | 'rejected') => {
    if (menuRowId) {
      setPositionStatuses(prev => ({
        ...prev,
        [menuRowId]: status
      }));
    }
    handleMenuClose();
  };

  const togglePos = (id: number) => {
    setExpanded(prev => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Obsługa ESC do zamykania menu (sprawdź przed innymi warunkami)
    if (e.key === 'Escape' && menuOpen) {
      e.preventDefault();
      handleMenuClose();
      return;
    }
    
    // Jeśli focus jest w menu (dropdown), nie obsługuj żadnych klawiszy w tabeli
    if (document.activeElement && document.activeElement.closest('.MuiMenu-paper')) {
      e.stopPropagation();
      return;
    }
    // Jeśli fokus jest w polu wyszukiwania lub filtra – odpuść obsługę
    if(searchInputRef.current && searchInputRef.current.contains(e.target as Node)) return;
    if(filterSelectRef.current && (filterSelectRef.current as HTMLElement).contains && (filterSelectRef.current as HTMLElement).contains(e.target as Node)) return;
    // Zachowaj systemowy skrót Option/Alt + ArrowUp / ArrowDown (przełączanie kart/zakładek)
    if(e.altKey && (e.key==='ArrowUp' || e.key==='ArrowDown')) return;
    // Teraz możemy zatrzymać propagację, bo wiemy, że nie jest to systemowy skrót
    e.stopPropagation();
    // Jeśli edytujemy komórkę
    if(editingCell){
      // Jeśli edytujemy nakład RMS (editingCell.expenseId) – obsługujemy zapis lokalnie w InputBase,
      // więc globalny handler nie powinien nic robić poza Escape.
      if(editingCell.expenseId){
        if(e.key==='Escape'){
          setEditingCell(null);
          setSelectedCell(null);
        }
        return; // pomiń dalszą obsługę
      }

      // Edycja pozycji głównej – dotychczasowa logika
      if(e.key==='Enter'){
        onUpdatePosition({id:editingCell.posId, field:editingCell.field as any, value: editingCell.field==='quantity' || editingCell.field==='unitPrice' ? parseFloat(editingCell.value) || 0 : editingCell.value });
        setEditingCell(null);
      }
      if(e.key==='Escape'){
        setEditingCell(null);
        setSelectedCell(null);
      }
      return;
    }

    // Obsługa zwijania/rozwijania tylko, gdy NIE jesteśmy w trybie zaznaczenia komórki / edycji
    if((e.key==='ArrowRight'||e.key==='ArrowLeft') && selectedRowId && selectedRowId.startsWith('pos-') && !selectedCell && !editingCell){
      if(e.altKey || e.metaKey || e.ctrlKey) { /* pozwól systemowym skrótom */ return; }
      const id=parseInt(selectedRowId.split('-')[1]);
      const posObj=positions.find(p=>p.id===id);
      if(!posObj) return;
      if(e.key==='ArrowRight' && posObj.positionType==='detailed' && !expanded.has(id)){
        e.preventDefault(); togglePos(id); return;
      }
      if(e.key==='ArrowLeft' && posObj.positionType==='detailed' && expanded.has(id)){
        e.preventDefault(); togglePos(id); return;
      }
    }

    // Skróty globalne (poza edycją) s → search, f → filter
    if(!editingCell && !selectedCell && !((e.target as HTMLElement).tagName==='INPUT')){
      if((e.key==='s' || e.key==='S')){ e.preventDefault(); searchInputRef.current?.focus(); return; }
      if((e.key==='f' || e.key==='F')){ e.preventDefault(); filterSelectRef.current?.focus(); return; }
    }

    if(selectedCell){
      const posObj = positions.find(p=>p.id===selectedCell.posId);
      if(!posObj) return;

      // Obsługa komórek RMS (gdy expenseId jest ustawione)
      if(selectedCell.expenseId){
        const child = posObj.children?.find(c=>c.id===selectedCell.expenseId);
        if(!child) return;

        if(e.key==='Enter' || e.key==='F2'){
          if(selectedCell.field==='norm'){
            // normy edytujemy osobnym inputem
            setEditingRms({positionId:posObj.id, expenseId:child.id, value:child.norm.toString()});
            return;
          }

          let currentVal = '';
          switch(selectedCell.field){
            case 'description': currentVal = child.description; break;
            case 'unit': currentVal = child.unit; break;
            case 'rmsPrice': currentVal = child.rmsPrice.toString(); break;
            default: break;
          }
          setEditingCell({posId: posObj.id, expenseId: child.id, field: selectedCell.field, value: currentVal});
        } else if(e.key==='ArrowRight'){
          moveCell(posObj, 1);
        } else if(e.key==='ArrowLeft'){
          moveCell(posObj, -1);
        } else if(e.key==='Escape'){
          setSelectedCell(null);
        } else if(e.key.length===1 && ['description','unit','rmsPrice'].includes(selectedCell.field)){
          e.preventDefault();
          setEditingCell({posId: posObj.id, expenseId: child.id, field: selectedCell.field, value: e.key});
        }
        return;
      }

      // ----- Pozycje główne (bez expenseId) -----
      if(e.key==='Enter' || e.key==='F2'){
        // rozpocznij edycję pozycji głównej
        let currentVal: string = '';
        switch(selectedCell.field){
          case 'description': currentVal = posObj.description; break;
          case 'quantity': currentVal = posObj.quantity.toString(); break;
          case 'unit': currentVal = posObj.unit; break;
          case 'unitPrice': currentVal = (posObj as any).unitPrice?.toString() || '0'; break;
        }
        setEditingCell({posId: posObj.id, field: selectedCell.field, value: currentVal});
      } else if(e.key==='ArrowRight'){
        moveCell(posObj, 1);
      } else if(e.key==='ArrowLeft'){
        moveCell(posObj, -1);
      } else if(e.key==='Escape'){
        setSelectedCell(null);
      } else if(e.key.length===1){
        e.preventDefault();
        // Rozpocznij edycję od wpisanej litery (komórka jest już zaznaczona)
        setEditingCell({posId: posObj.id, field: selectedCell.field, value: e.key});
      }
      return;
    }

    // Brak zaznaczonej komórki – obsługa wierszy
    if(!selectedRowId && (e.key==='ArrowDown'||e.key==='ArrowUp')){
      const container = tableContainerRef.current;
      if(container){
        const allRows = Array.from(container.querySelectorAll('tr[data-rowid]')) as HTMLElement[];
        const firstVisible = allRows.find(r=> r.offsetParent!==null);
        if(firstVisible){
          const idPart = firstVisible.dataset.rowid?.split('-')[1];
          if(idPart) onSelectRow(`pos-${idPart}`);
        }
      }
      return;
    }

    if(e.altKey || e.metaKey || e.ctrlKey){ /* system shortcuts */ return; }
    const rows = Array.from(document.querySelectorAll('[data-rowid]')) as HTMLElement[];
    const currentIndex = rows.findIndex(r => r.dataset.rowid === selectedRowId);
    if (e.key === 'ArrowDown') {
      if(e.altKey) return;
      e.preventDefault();
      if(currentIndex < rows.length -1){
        const nextRow = rows[currentIndex + 1];
        onSelectRow(nextRow.dataset.rowid || '');
        const container = tableContainerRef.current;
        if(container){
          const rowBottom = nextRow.offsetTop + nextRow.offsetHeight;
          if(rowBottom > container.scrollTop + container.clientHeight){
            container.scrollTop = rowBottom - container.clientHeight;
          }
        }
      }
    }
    if (e.key === 'ArrowUp') {
      if(e.altKey) return;
      e.preventDefault();
      if(currentIndex > 0){
        const prevRow = rows[currentIndex - 1];
        onSelectRow(prevRow.dataset.rowid || '');
        const container = tableContainerRef.current;
        if(container){
          const rowTop = prevRow.offsetTop;
          if(rowTop < container.scrollTop){
            const headerHeight = (container.querySelector('thead') as HTMLElement)?.offsetHeight || 0;
            if(rowTop < container.scrollTop + headerHeight){
              container.scrollTop = rowTop - headerHeight;
            }
          }
        }
      }
    }
    if (e.key === 'Enter' || e.key==='F2'){
      // rozpocznij tryb zaznaczenia komórki
      const parts = selectedRowId?.split('-');
      if(parts?.[0]==='pos'){
        const id = parseInt(parts[1]);
        setEditingCell(null);
        setSelectedCell({posId:id, field:'description'});
      } else if(parts?.[0]==='exp'){
        const expId = parseInt(parts[1]);
        const parentPos = positions.find(p=> (p.children??[]).some(c=>c.id===expId));
        if(!parentPos) return;
        const child = parentPos.children?.find(c=>c.id===expId);
        if(!child) return;
        if(['Kp','Kz','Z'].includes(child.type)) return; // nie edytujemy procentowych wierszy
        setEditingCell(null);
        setSelectedCell({posId:parentPos.id, expenseId:expId, field:'description'});
      }
      return;
    }
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      const parts = selectedRowId?.split('-');
      if (parts?.[0]==='pos') {
        const id = parseInt(parts[1]);
        const posObj = positions.find(p=>p.id===id);
        if (posObj?.positionType==='detailed' && !expanded.has(id)) togglePos(id);
      }
    }
    if (e.key === 'ArrowLeft' && selectedRowId && selectedRowId.startsWith('exp-')) {
      const expId = parseInt(selectedRowId.split('-')[1]);
      const parentPos = positions.find(p => (p.children ?? []).some(c => c.id === expId));
      if (parentPos && expanded.has(parentPos.id)) {
        e.preventDefault();
        setExpanded(prev => {
          const n = new Set(prev);
          n.delete(parentPos.id);
          return n;
        });
        onSelectRow(`pos-${parentPos.id}`);
        return;
      }
    }
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      const parts = selectedRowId?.split('-');
      if (parts?.[0]==='pos') {
        const id = parseInt(parts[1]);
        const posObj = positions.find(p=>p.id===id);
        if (posObj?.positionType==='detailed' && expanded.has(id)) togglePos(id);
      }
    }
    // Obsługa klawisza 'e' (otwórz menu akcji dla zaznaczonego wiersza)
    if (!editingCell && !selectedCell && selectedRowId && (e.key === 'e' || e.key === 'E')) {
      e.preventDefault();
      // Znajdź przycisk menu trzy kropki w zaznaczonym wierszu
      const rowEl = document.querySelector(`[data-rowid="${selectedRowId}"]`) as HTMLElement;
      if (rowEl) {
        // Szukaj przycisku w kolumnie actions (ostatnia kolumna)
        const actionsCell = rowEl.querySelector('td:last-child') as HTMLElement;
        if (actionsCell) {
          const menuButton = actionsCell.querySelector('button') as HTMLElement;
          if (menuButton) {
            const syntheticEvent = {
              currentTarget: menuButton,
              preventDefault: () => {},
              stopPropagation: () => {}
            } as React.MouseEvent<HTMLButtonElement>;
            handleMenuOpen(syntheticEvent, selectedRowId);
          } else {
            // Fallback: użyj wiersza jako anchor
            setMenuRowId(selectedRowId);
            setMenuAnchorEl(rowEl);
          }
        } else {
          // Fallback: użyj wiersza jako anchor
          setMenuRowId(selectedRowId);
          setMenuAnchorEl(rowEl);
        }
      }
      return;
    }
    
    // Obsługa ESC do zamykania menu (sprawdź przed innymi warunkami)
    if (e.key === 'Escape' && menuOpen) {
      e.preventDefault();
      handleMenuClose();
      return;
    }
    // Jeśli menu akcji jest otwarte, zablokuj obsługę klawiatury w głównym handlerze
    // (obsługa jest w MenuListProps.onKeyDown)
    if (menuOpen && document.activeElement?.closest('.MuiMenu-paper')) {
      return;
    }
    if ((e.key === 'w' || e.key === 'W') && !editingCell && document.activeElement?.tagName !== 'INPUT') {
      e.preventDefault();
      setGroupView(prev => !prev);
    }
  };

  // Global ESC handler – gwarantuje wyjście z edycji nawet jeśli input przechwytuje zdarzenie
  React.useEffect(() => {
    const escListener = (e: KeyboardEvent) => {
      if(e.key === 'Escape'){
        if(menuOpen){
          handleMenuClose();
        } else if(editingCell){
          setEditingCell(null);
          setSelectedCell(null);
        } else if(selectedCell){
          setSelectedCell(null);
        }
      }
    };
    document.addEventListener('keydown', escListener);
    return () => document.removeEventListener('keydown', escListener);
  }, [editingCell, selectedCell, menuOpen]);

  // Po wyjściu z edycji przywracamy fokus na aktualny wiersz, aby klawisze nawigacji działały
  React.useEffect(() => {
    if(!editingCell && !selectedCell && selectedRowId){
      const rowEl = document.querySelector(`[data-rowid="${selectedRowId}"]`) as HTMLElement | null;
      rowEl?.focus();
    }
  }, [editingCell, selectedCell, selectedRowId]);

  // Obsługa klawiszy ⌘Z i ⌘Y
  React.useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey && e.key.toLowerCase() === 'z') { e.preventDefault(); if (canUndo) undo(); }
      if (((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'y') || ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === 'z')) { e.preventDefault(); if (canRedo) redo(); }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [canUndo, canRedo, undo, redo]);

  // Global listener for Arrow Left / Right to expand-collapse positions
  React.useEffect(()=>{
    const onKey=(e:KeyboardEvent)=>{
      if(e.key!=='ArrowRight' && e.key!=='ArrowLeft') return;
      if(e.altKey || e.metaKey || e.ctrlKey) return; // nie przechwytuj Option/Alt + ←/→
      const tag=(e.target as HTMLElement).tagName;
      if(tag==='INPUT'||tag==='TEXTAREA'||(e.target as HTMLElement).isContentEditable) return;
      if(selectedCell || editingCell) return; // nie rozpinaj pozycji, gdy trwa wybór/edycja komórki
      if(!selectedRowId) return;
      const parts=selectedRowId.split('-');
      if(parts[0]!=='pos') return;
      const id=parseInt(parts[1]);
      const posObj=positions.find(p=>p.id===id);
      if(!posObj||posObj.positionType!=='detailed') return;
      if(e.key==='ArrowRight' && !expanded.has(id)) { e.preventDefault(); togglePos(id);} 
      if(e.key==='ArrowLeft' && expanded.has(id)) { e.preventDefault(); togglePos(id);} 
    };
    window.addEventListener('keydown', onKey);
    return ()=> window.removeEventListener('keydown', onKey);
  }, [selectedRowId, expanded, positions, selectedCell, editingCell]);

  // Sortowanie
  const [sortField, setSortField] = useState<'nr'|'description'|'quantity'|'unitPrice'|'value'>('nr');
  const [sortDir, setSortDir] = useState<'asc'|'desc'>('asc');

  const handleSort = (field: 'nr'|'description'|'quantity'|'unitPrice'|'value') => {
    if(sortField===field){
      setSortDir(prev=> prev==='asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  // Funkcja pomocnicza – oblicza cenę jednostkową pozycji
  const computeUnitPrice = React.useCallback((position:EstimatePosition):number => {
    if(position.positionType==='simplified'){
      return (position as any).unitPrice || 0;
    }
    const childArr = position.children ?? [];
    const sumR = childArr.filter(c=>c.type==='R').reduce((s,c)=>s + c.norm*c.rmsPrice,0);
    const sumM = childArr.filter(c=>c.type==='M').reduce((s,c)=>s + c.norm*c.rmsPrice,0);
    const sumS = childArr.filter(c=>c.type==='S').reduce((s,c)=>s + c.norm*c.rmsPrice,0);
    // Używaj dynamicznie aktualnych narzutów z nagłówka (markups), a nie z wierszy Kp/Kz/Z
    const kpUnit = (Number.isFinite((markups as any)?.kp) ? (markups as any).kp : 0)/100 * sumR;
    const kzUnit = (Number.isFinite((markups as any)?.kz) ? (markups as any).kz : 0)/100 * sumM;
    const zUnit  = (Number.isFinite((markups as any)?.z)  ? (markups as any).z  : 0)/100 * (sumR+sumM+sumS+kpUnit+kzUnit);
    return sumR+sumM+sumS+kpUnit+kzUnit+zUnit;
  }, [markups]);

  const filteredPositions = positions.filter(p => {
    const desc = (p.description || '').toLowerCase();
    const nrStr = (p.nr ?? '').toString();
    const txt = desc.includes(search.toLowerCase()) || nrStr.includes(search);
    if(sectionFilter==='all') return txt;
    const sec = getSectionForNr(nrStr, tesRows);
    return txt && sec && sec.id.toString()===sectionFilter;
  });

  const sortedPositions = React.useMemo(()=>{
    const arr = [...filteredPositions];
    arr.sort((a,b)=>{
      let av: any, bv: any;
      switch(sortField){
        case 'nr': av=parseInt(a.nr); bv=parseInt(b.nr); break;
        case 'description': av=a.description.toLowerCase(); bv=b.description.toLowerCase(); break;
        case 'quantity': av=a.quantity; bv=b.quantity; break;
        case 'unitPrice': {
          av = a.positionType==='simplified' ? ((a as any).unitPrice || 0) : computeUnitPrice(a);
          bv = b.positionType==='simplified' ? ((b as any).unitPrice || 0) : computeUnitPrice(b);
        } break;
        case 'value': {
          const unitA = a.positionType==='simplified' ? ((a as any).unitPrice || 0) : computeUnitPrice(a);
          const unitB = b.positionType==='simplified' ? ((b as any).unitPrice || 0) : computeUnitPrice(b);
          const valA = unitA * a.quantity;
          const valB = unitB * b.quantity;
          av = valA; bv = valB;
        } break;
      }
      if(av < bv) return sortDir==='asc' ? -1 : 1;
      if(av > bv) return sortDir==='asc' ? 1 : -1;
      return 0;
    });
    return arr;
  }, [filteredPositions, sortField, sortDir, computeUnitPrice]);

  // Przygotuj listę wierszy z nagłówkami działów (jeśli groupView=true)
  // Gdy groupView=true: pozycje wplecione w działy, bez sortowania, numeracja X.d.Y
  const rowsWithHeaders = React.useMemo(() => {
    type RowItem = { type: 'header' | 'pos'; section?: TesSection; pos?: EstimatePosition; displayNr?: string };
    // Widok bez działów – użyj sortowania
    if (!groupView) {
      return sortedPositions.map(p => ({ type: 'pos' as const, pos: p, displayNr: p.nr }));
    }
    // Widok z działami – pozycje wplecione w działy, kolejność: działy → pozycje wg nr w dziale
    if (!tesRows || tesRows.length === 0) {
      return filteredPositions
        .sort((a, b) => parseInt(a.nr) - parseInt(b.nr))
        .map(p => ({ type: 'pos' as const, pos: p, displayNr: p.nr }));
    }
    const res: RowItem[] = [];
    const sortedSecs = [...tesRows].sort((a, b) => compareSectionNr(a.nr, b.nr));
    const assignedPosIds = new Set<number>();
    let posCounter = 1; // Ciągła numeracja pozycji w całym kosztorysie

    sortedSecs.forEach(sec => {
      const inSec = filteredPositions
        .filter(p => {
          const best = findBestSection(parseInt(p.nr), tesRows);
          return best?.id === sec.id;
        })
        .sort((a, b) => parseInt(a.nr) - parseInt(b.nr));
      // Gdy filtr działu aktywny: pomiń działy bez pozycji w filteredPositions
      if (sectionFilter !== 'all' && inSec.length === 0) return;
      res.push({ type: 'header', section: sec });
      inSec.forEach((p) => {
        assignedPosIds.add(p.id);
        res.push({ type: 'pos', pos: p, displayNr: `${posCounter}.d.${sec.nr}` });
        posCounter++;
      });
    });

    // Pozycje bez przypisanego działu – na końcu
    const unassigned = filteredPositions
      .filter(p => !assignedPosIds.has(p.id))
      .sort((a, b) => parseInt(a.nr) - parseInt(b.nr));
    if (unassigned.length > 0) {
      res.push({ type: 'header', section: { id: -1, nr: '0', range: '', name: 'Bez działu', total: 0 } as TesSection });
      unassigned.forEach((p) => {
        res.push({ type: 'pos', pos: p, displayNr: `${posCounter}.d.0` });
        posCounter++;
      });
    }
    return res;
  }, [groupView, tesRows, filteredPositions, sortedPositions, sectionFilter]);

  const menuItemRefs = useRef<(HTMLLIElement | null)[]>([]);

  React.useEffect(() => {
    if (menuOpen) {
      // Ustaw fokus na zaznaczony element menu po krótkim opóźnieniu (aby Menu się otworzyło)
      const timer = setTimeout(() => {
        const selectedRef = menuItemRefs.current[selectedMenuItemIndex];
        if (selectedRef) {
          selectedRef.focus();
        } else {
          // Fallback: ustaw focus na pierwszym elemencie
          const firstRef = menuItemRefs.current[0];
          if (firstRef) {
            firstRef.focus();
          }
        }
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [menuOpen, selectedMenuItemIndex]);

  // Style dla selecta zgodne z aplikacją
  const getSelectSx = () => ({
    '& .MuiOutlinedInput-root': {
      height: 36,
      backgroundColor: '#fff',
      borderRadius: '4px'
    },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#2020AF !important' },
    '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' },
    '&:has(.MuiSelect-root[aria-expanded="true"]) .MuiOutlinedInput-notchedOutline': { borderColor: '#2020AF !important' },
    '& .MuiSelect-select': {
      padding: '8px 12px',
      boxSizing: 'border-box',
      fontFamily: 'Roboto, sans-serif',
      fontSize: '14px',
      color: sectionFilter === 'all' ? '#B9B9B9' : '#2D2D32',
      lineHeight: '20px',
      minHeight: '20px',
      display: 'flex',
      alignItems: 'center'
    }
  });

  // Style dla pola wyszukiwania zgodne z aplikacją
  const searchSx = {
    '& .MuiOutlinedInput-root': {
      height: 36,
      backgroundColor: '#fff',
      borderRadius: '4px'
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#d4d4d9'
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#595966'
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#595966 !important'
    },
    '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' },
    '& .MuiInputBase-input': { px: '12px', fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#2D2D32' },
    '& .MuiInputBase-input::placeholder': { color: '#B9B9B9', opacity: 1 }
  } as const;

  // Style sortowania zgodne z aplikacją
  const sortLabelBaseSx = {
    fontFamily: 'Roboto, sans-serif',
    fontWeight: 400,
    fontSize: '16px',
    color: '#2D2D32',
    display: 'inline-flex',
    alignItems: 'center',
    flexDirection: 'row',
    gap: '4px',
    '& .MuiTableSortLabel-icon': {
      opacity: 1,
      width: 12,
      height: 12,
      ml: '4px'
    }
  } as const;
  const StrongSortIcon = () => (<UnfoldMore sx={{ width: 12, height: 12, color: '#2D2D32' }} />);
  const MutedSortIcon = () => (<UnfoldMore sx={{ width: 12, height: 12, color: '#d4d4d9' }} />);
  const getSortLabelProps = (isActive: boolean) => ({
    IconComponent: (isActive ? StrongSortIcon : MutedSortIcon) as any,
    sx: {
      ...sortLabelBaseSx,
      '& .MuiTableSortLabel-icon': {
        ...(sortLabelBaseSx as any)['& .MuiTableSortLabel-icon'],
        color: isActive ? '#2D2D32' : '#d4d4d9'
      }
    }
  }) as const;

  // Funkcja dzieląca basis na podstawę wyceny i załącznik
  const parseBasis = (basis: string): { base: string; attachment: string | null } => {
    if (!basis) return { base: '', attachment: null };
    
    // Szukamy ostatniego numeru w formacie NNNN-NNNN (np. "0535-0400")
    // Wszystko do tego numeru to podstawa, wszystko po nim to załącznik
    const numberPattern = /\s+(\d{4}-\d{4})(?:\s+(.+))?$/;
    const match = basis.match(numberPattern);
    
    if (match) {
      const numberIndex = basis.lastIndexOf(match[1]);
      const base = basis.substring(0, numberIndex + match[1].length).trim();
      const attachment = match[2] ? match[2].trim() : null;
      return { base, attachment };
    }
    
    // Jeśli nie znaleziono wzorca, zwróć całość jako podstawę
    return { base: basis, attachment: null };
  };

  return (
    <>
      <Box sx={{ position: 'relative', width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ 
        width: '100%',
        backgroundColor: '#FFFFFF',
        border: '1px solid #D4D4D9',
        borderRadius: '4px',
        padding: '8px',
        overflowX: 'hidden',
        marginTop: '30px',
        display: 'flex',
        flexDirection: 'column',
        flex: '1 1 auto',
        minHeight: 0,
        overflow: 'hidden'
      }} onKeyDown={handleKeyDown} tabIndex={0}>
      {/* Toolbar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexShrink: 0 }}>
        <Tooltip title="Cofnij (⌘Z)">
          <span>
            <IconButton size="small" onClick={undo} disabled={!canUndo}>
              <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#2D2D32">
                <path d="M318.08-220q-12.77 0-21.39-8.62-8.61-8.61-8.61-21.38t8.61-21.38q8.62-8.62 21.39-8.62h257.07q62.62 0 107.77-41.35 45.16-41.34 45.16-102.11 0-60.77-45.16-101.93-45.15-41.15-107.77-41.15H294.31l90.23 90.23q8.69 8.7 8.69 21.08 0 12.38-8.69 21.08-8.69 8.69-21.27 8.5-12.58-.2-20.88-8.5L205.31-571.23q-5.62-5.62-7.92-11.85-2.31-6.23-2.31-13.46t2.31-13.46q2.3-6.23 7.92-11.85l137.08-137.07q8.69-8.69 21.07-8.69 12.39 0 21.08 8.69 8.69 8.69 8.5 21.27-.19 12.57-8.5 20.88l-90.23 90.23h280.84q87.77 0 150.35 58.58t62.58 144.5q0 85.92-62.58 144.69Q662.92-220 575.15-220H318.08Z"/>
              </svg>
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Ponów (⌘Y)">
          <span>
            <IconButton size="small" onClick={redo} disabled={!canRedo}>
              <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#2D2D32">
                <path d="M665.69-566.54H384.85q-62.62 0-107.77 41.15-45.16 41.16-45.16 101.93 0 60.77 45.16 102.11Q322.23-280 384.85-280h257.07q12.77 0 21.39 8.62 8.61 8.61 8.61 21.38t-8.61 21.38q-8.62 8.62-21.39 8.62H384.85q-87.77 0-150.35-58.77t-62.58-144.69q0-85.92 62.58-144.5t150.35-58.58h280.84l-90.23-90.23q-8.31-8.31-8.5-20.88-.19-12.58 8.5-21.27t21.08-8.69q12.38 0 21.07 8.69l137.08 137.07q5.62 5.62 7.92 11.85 2.31 6.23 2.31 13.46t-2.31 13.46q-2.3 6.23-7.92 11.85L617.61-434.15q-8.3 8.3-20.88 8.5-12.58.19-21.27-8.5-8.69-8.7-8.69-21.08 0-12.38 8.69-21.08l90.23-90.23Z"/>
              </svg>
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Dodaj dział">
          <IconButton size="small" onClick={onAddDivision} sx={{ color: '#2D2D32' }}>D</IconButton>
        </Tooltip>
        <Tooltip title="Dodaj pozycję">
          <IconButton size="small" onClick={onAddPosition} sx={{ color: '#2D2D32' }}>P</IconButton>
        </Tooltip>
        <Tooltip title="Dodaj robociznę">
          <IconButton size="small" onClick={onAddLabor} sx={{ color: '#2D2D32' }}>R</IconButton>
        </Tooltip>
        <Tooltip title="Dodaj sprzęt">
          <IconButton size="small" onClick={onAddEquipment} sx={{ color: '#2D2D32' }}>S</IconButton>
        </Tooltip>
        <Tooltip title="Dodaj materiał">
          <IconButton size="small" onClick={onAddMaterial} sx={{ color: '#2D2D32' }}>M</IconButton>
        </Tooltip>

        {/* Przełącznik widoku działów */}
        <Tooltip title={groupView ? 'Widok bez działów' : 'Widok z działami'}>
          <IconButton size="small" onClick={() => setGroupView(prev => !prev)} sx={{ color: '#2D2D32' }}>
            {groupView ? <TableRows fontSize="small" sx={{ color: '#2D2D32' }} /> : <TableChart fontSize="small" sx={{ color: '#2D2D32' }} />}
          </IconButton>
        </Tooltip>

        {/* Generuj Google Sheet z kosztorysem i otwórz w nowej karcie */}
        <Tooltip title="Generuj Google Sheet">
          <IconButton
            size="small"
            disabled={sheetGenerating}
            sx={{
              width: 32,
              height: 32,
              borderRadius: '4px',
              p: 0,
              '&:hover': { bgcolor: 'rgba(0,0,0,0.06)' }
            }}
            onClick={handleGenerateGoogleSheet}
          >
            <Box
              component="img"
              alt="Generuj Google Sheet"
              src="/assets/icons/sheets_2020q4_48dp.png"
              sx={{ width: 24, height: 24 }}
            />
          </IconButton>
        </Tooltip>

        {/* Eksport kosztorysu do EasyEstimate (działy, pozycje, jm, ilość, cena jedn.) */}
        <Tooltip title="Eksportuj do EasyEstimate">
          <IconButton
            size="small"
            sx={{
              width: 32,
              height: 32,
              borderRadius: '4px',
              p: 0,
              color: '#2D2D32',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.06)' },
            }}
            onClick={() => setExportEeOpen(true)}
          >
            <svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 -960 960 960" fill="currentColor">
              <path d="M440-200h80v-167l64 64 56-57-160-160-160 160 57 56 63-63v167ZM240-80q-33 0-56.5-23.5T160-160v-640q0-33 23.5-56.5T240-880h320l240 240v480q0 33-23.5 56.5T720-80H240Zm280-520v-200H240v640h480v-440H520ZM240-800v200-200 640-640Z"/>
            </svg>
          </IconButton>
        </Tooltip>

        {/* Ikona zarządzania kolumnami */}
        <Tooltip title="Dostosuj kolumny">
          <IconButton
            size="small"
            sx={{
              width: 32,
              height: 32,
              borderRadius: '4px',
              p: 0,
              bgcolor: colsOpen ? '#E0E0F5' : 'transparent',
              transition: 'background-color 0.15s ease',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.06)' }
            }}
            onClick={(e) => {
              setDraftColumnsVisible({ ...columnsVisible });
              setDraftWidths({ ...columnWidths });
              setColsAnchorEl(e.currentTarget);
              setColResizeMode(true);
            }}
          >
            <Box
              component="img"
              alt="kolumny"
              src="/assets/icons/frame-344.svg?v=2"
              sx={{ width: 32, height: 32 }}
            />
          </IconButton>
        </Tooltip>

        <Box sx={{ display:'flex', alignItems:'center', gap:1, ml:'auto' }}>
          <Select
            size="small"
            value={sectionFilter}
            onChange={e=>setSectionFilter(e.target.value)}
            displayEmpty
            renderValue={(value) => {
              if (value === 'all') return 'Wszystkie działy';
              if (!value || value === '') return 'Wybierz z listy';
              const section = tesRows.find(sec => sec.id.toString() === value);
              return section ? `${section.nr} – ${section.name}` : 'Wybierz z listy';
            }}
            sx={{ minWidth:300, ...getSelectSx() }}
            inputRef={filterSelectRef}
            onKeyDown={e => {
              e.stopPropagation();
              if (e.key === 'Escape') {
                tableContainerRef.current?.focus();
              }
            }}
          >
            <MenuItem value="all">Wszystkie działy</MenuItem>
            {tesRows.map(sec=> (
              <MenuItem key={sec.id} value={sec.id.toString()}>{sec.nr} – {sec.name}</MenuItem>
            ))}
          </Select>

          <TextField 
            size="small" 
            inputRef={searchInputRef} 
            placeholder="Szukaj pozycji" 
            value={search} 
            onChange={e=>setSearch(e.target.value)} 
            sx={{ width:300, ...searchSx }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ width: 20, height: 20, color: '#595966' }} />
                </InputAdornment>
              )
            }}
            onKeyDown={e => {
              e.stopPropagation();
              if (e.key === 'Escape') {
                tableContainerRef.current?.focus();
              }
            }}
          />
        </Box>
      </Box>

      <TableContainer
        component={Box}
        ref={tableContainerRef}
        tabIndex={0}
        onKeyDown={handleKeyDown}
        sx={{ 
          flex: '1 1 0',
          minHeight: 0,
          overflowY:'auto',
          overflowX:'hidden',
          borderRadius:0, 
          position: 'relative',
          '&:focus':{outline:'none'},
          marginLeft: '-8px',
          marginRight: '-8px',
          width: 'calc(100% + 16px)',
          paddingBottom: '48px' // Space for fixed bottom bar (40px height + 8px padding)
        }}
      >
        <Table stickyHeader size="small" sx={{ 
          tableLayout:'auto', 
          fontSize:'0.70rem',
          '& .MuiTableCell-root': { 
            padding:'3px 6px', 
            borderBottom: '1px solid #EDEDED',
            color: '#2D2D32'
          },
          '& thead .MuiTableCell-root': { 
            fontSize:'16px',
            fontWeight:400,
            fontFamily: 'Roboto, sans-serif',
            borderTop: '1px solid #EDEDED',
            position: 'sticky',
            top: 0,
            zIndex: 10,
            backgroundColor: '#FAFAFA',
            '&:first-of-type': { paddingLeft: '16px' },
            '&:last-of-type': { paddingRight: '16px' }
          },
          '& tbody .MuiTableRow-root': { borderBottom: '1px solid #EDEDED' }
        }}>
          <TableHead>
            <TableRow sx={{
              backgroundColor: '#FAFAFA',
              borderTop: '1px solid #EDEDED',
              borderBottom: '1px solid #EDEDED',
              position: 'sticky',
              top: 0,
              zIndex: 10
            }}>
              {colV('nr') && (
                <TableCell sx={{ cursor: groupView ? 'default' : 'pointer', color: '#2D2D32', width: getColWidthPx('nr'), position: 'relative' }} onClick={()=> !groupView && handleSort('nr')}>
                  {groupView ? 'Nr' : <TableSortLabel {...getSortLabelProps(sortField==='nr')} active={sortField==='nr'} direction={sortField==='nr' ? sortDir : 'asc'}>Nr</TableSortLabel>}
                  {colResizeMode && (
                    <Box
                      role="separator"
                      aria-orientation="vertical"
                      sx={{
                        position: 'absolute',
                        right: 0,
                        top: 0,
                        bottom: 0,
                        width: '12px',
                        cursor: 'col-resize',
                        zIndex: 10,
                        touchAction: 'none',
                        backgroundImage: dragCol?.key === 'nr' ? 'linear-gradient(to right, transparent 45%, #2020AF 45%, #2020AF 55%, transparent 55%)' : 'linear-gradient(to right, transparent 45%, #FF0000 45%, #FF0000 55%, transparent 55%)',
                        '&:hover': { backgroundColor: 'rgba(0,0,0,0.06)' }
                      }}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setDragCol({ key: 'nr', startX: e.clientX, startW: columnWidths['nr'] ?? defaultColumnWidths['nr'] });
                      }}
                    />
                  )}
                </TableCell>
              )}
              {colV('type') && (
                <TableCell sx={{ color: '#2D2D32', width: getColWidthPx('type'), position: 'relative' }}>T
                  {colResizeMode && (
                    <Box
                      role="separator"
                      aria-orientation="vertical"
                      sx={{
                        position: 'absolute',
                        right: 0,
                        top: 0,
                        bottom: 0,
                        width: '12px',
                        cursor: 'col-resize',
                        zIndex: 10,
                        touchAction: 'none',
                        backgroundImage: dragCol?.key === 'type' ? 'linear-gradient(to right, transparent 45%, #2020AF 45%, #2020AF 55%, transparent 55%)' : 'linear-gradient(to right, transparent 45%, #FF0000 45%, #FF0000 55%, transparent 55%)',
                        '&:hover': { backgroundColor: 'rgba(0,0,0,0.06)' }
                      }}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setDragCol({ key: 'type', startX: e.clientX, startW: columnWidths['type'] ?? defaultColumnWidths['type'] });
                      }}
                    />
                  )}
                </TableCell>
              )}
              {colV('description') && (
                <TableCell sx={{ whiteSpace:'normal', wordBreak:'break-word', cursor: groupView ? 'default' : 'pointer', color: '#2D2D32', width: getColWidthPx('description'), position: 'relative' }} onClick={()=> !groupView && handleSort('description')}>
                  {groupView ? 'Podstawa, opis, elementy' : <TableSortLabel {...getSortLabelProps(sortField==='description')} active={sortField==='description'} direction={sortField==='description' ? sortDir : 'asc'}>Podstawa, opis, elementy</TableSortLabel>}
                  {colResizeMode && (
                    <Box
                      role="separator"
                      aria-orientation="vertical"
                      sx={{
                        position: 'absolute',
                        right: 0,
                        top: 0,
                        bottom: 0,
                        width: '12px',
                        cursor: 'col-resize',
                        zIndex: 10,
                        touchAction: 'none',
                        backgroundImage: dragCol?.key === 'description' ? 'linear-gradient(to right, transparent 45%, #2020AF 45%, #2020AF 55%, transparent 55%)' : 'linear-gradient(to right, transparent 45%, #FF0000 45%, #FF0000 55%, transparent 55%)',
                        '&:hover': { backgroundColor: 'rgba(0,0,0,0.06)' }
                      }}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setDragCol({ key: 'description', startX: e.clientX, startW: columnWidths['description'] ?? defaultColumnWidths['description'] });
                      }}
                    />
                  )}
                </TableCell>
              )}
              {colV('quantity') && (
                <TableCell align="right" sx={{ cursor: groupView ? 'default' : 'pointer', color: '#2D2D32', width: getColWidthPx('quantity'), position: 'relative' }} onClick={()=> !groupView && handleSort('quantity')}>
                  {groupView ? 'Ilość' : <TableSortLabel {...getSortLabelProps(sortField==='quantity')} active={sortField==='quantity'} direction={sortField==='quantity' ? sortDir : 'asc'}>Ilość</TableSortLabel>}
                  {colResizeMode && (
                    <Box
                      role="separator"
                      aria-orientation="vertical"
                      sx={{
                        position: 'absolute',
                        right: 0,
                        top: 0,
                        bottom: 0,
                        width: '12px',
                        cursor: 'col-resize',
                        zIndex: 10,
                        touchAction: 'none',
                        backgroundImage: dragCol?.key === 'quantity' ? 'linear-gradient(to right, transparent 45%, #2020AF 45%, #2020AF 55%, transparent 55%)' : 'linear-gradient(to right, transparent 45%, #FF0000 45%, #FF0000 55%, transparent 55%)',
                        '&:hover': { backgroundColor: 'rgba(0,0,0,0.06)' }
                      }}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setDragCol({ key: 'quantity', startX: e.clientX, startW: columnWidths['quantity'] ?? defaultColumnWidths['quantity'] });
                      }}
                    />
                  )}
                </TableCell>
              )}
              {colV('unit') && (
                <TableCell sx={{ color: '#2D2D32', width: getColWidthPx('unit'), position: 'relative' }}>Jmn
                  {colResizeMode && (
                    <Box
                      role="separator"
                      aria-orientation="vertical"
                      sx={{
                        position: 'absolute',
                        right: 0,
                        top: 0,
                        bottom: 0,
                        width: '12px',
                        cursor: 'col-resize',
                        zIndex: 10,
                        touchAction: 'none',
                        backgroundImage: dragCol?.key === 'unit' ? 'linear-gradient(to right, transparent 45%, #2020AF 45%, #2020AF 55%, transparent 55%)' : 'linear-gradient(to right, transparent 45%, #FF0000 45%, #FF0000 55%, transparent 55%)',
                        '&:hover': { backgroundColor: 'rgba(0,0,0,0.06)' }
                      }}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setDragCol({ key: 'unit', startX: e.clientX, startW: columnWidths['unit'] ?? defaultColumnWidths['unit'] });
                      }}
                    />
                  )}
                </TableCell>
              )}
              {colV('norm') && (
                <TableCell align="right" sx={{ color: '#2D2D32', width: getColWidthPx('norm'), position: 'relative' }}>Norma
                  {colResizeMode && (
                    <Box
                      role="separator"
                      aria-orientation="vertical"
                      sx={{
                        position: 'absolute',
                        right: 0,
                        top: 0,
                        bottom: 0,
                        width: '12px',
                        cursor: 'col-resize',
                        zIndex: 10,
                        touchAction: 'none',
                        backgroundImage: dragCol?.key === 'norm' ? 'linear-gradient(to right, transparent 45%, #2020AF 45%, #2020AF 55%, transparent 55%)' : 'linear-gradient(to right, transparent 45%, #FF0000 45%, #FF0000 55%, transparent 55%)',
                        '&:hover': { backgroundColor: 'rgba(0,0,0,0.06)' }
                      }}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setDragCol({ key: 'norm', startX: e.clientX, startW: columnWidths['norm'] ?? defaultColumnWidths['norm'] });
                      }}
                    />
                  )}
                </TableCell>
              )}
              {colV('expenses') && (
                <TableCell align="right" sx={{ color: '#2D2D32', width: getColWidthPx('expenses'), position: 'relative' }}>Nakłady
                  {colResizeMode && (
                    <Box
                      role="separator"
                      aria-orientation="vertical"
                      sx={{
                        position: 'absolute',
                        right: 0,
                        top: 0,
                        bottom: 0,
                        width: '12px',
                        cursor: 'col-resize',
                        zIndex: 10,
                        touchAction: 'none',
                        backgroundImage: dragCol?.key === 'expenses' ? 'linear-gradient(to right, transparent 45%, #2020AF 45%, #2020AF 55%, transparent 55%)' : 'linear-gradient(to right, transparent 45%, #FF0000 45%, #FF0000 55%, transparent 55%)',
                        '&:hover': { backgroundColor: 'rgba(0,0,0,0.06)' }
                      }}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setDragCol({ key: 'expenses', startX: e.clientX, startW: columnWidths['expenses'] ?? defaultColumnWidths['expenses'] });
                      }}
                    />
                  )}
                </TableCell>
              )}
              {colV('rmsPrice') && (
                <TableCell align="right" sx={{ color: '#2D2D32', width: getColWidthPx('rmsPrice'), position: 'relative' }}>C.RMS
                  {colResizeMode && (
                    <Box
                      role="separator"
                      aria-orientation="vertical"
                      sx={{
                        position: 'absolute',
                        right: 0,
                        top: 0,
                        bottom: 0,
                        width: '12px',
                        cursor: 'col-resize',
                        zIndex: 10,
                        touchAction: 'none',
                        backgroundImage: dragCol?.key === 'rmsPrice' ? 'linear-gradient(to right, transparent 45%, #2020AF 45%, #2020AF 55%, transparent 55%)' : 'linear-gradient(to right, transparent 45%, #FF0000 45%, #FF0000 55%, transparent 55%)',
                        '&:hover': { backgroundColor: 'rgba(0,0,0,0.06)' }
                      }}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setDragCol({ key: 'rmsPrice', startX: e.clientX, startW: columnWidths['rmsPrice'] ?? defaultColumnWidths['rmsPrice'] });
                      }}
                    />
                  )}
                </TableCell>
              )}
              {colV('unitPrice') && (
                <TableCell align="right" sx={{ cursor: groupView ? 'default' : 'pointer', color: '#2D2D32', width: getColWidthPx('unitPrice'), position: 'relative' }} onClick={()=> !groupView && handleSort('unitPrice')}>
                  {groupView ? 'Cena jedn.' : <TableSortLabel {...getSortLabelProps(sortField==='unitPrice')} active={sortField==='unitPrice'} direction={sortField==='unitPrice' ? sortDir : 'asc'}>Cena jedn.</TableSortLabel>}
                  {colResizeMode && (
                    <Box
                      role="separator"
                      aria-orientation="vertical"
                      sx={{
                        position: 'absolute',
                        right: 0,
                        top: 0,
                        bottom: 0,
                        width: '12px',
                        cursor: 'col-resize',
                        zIndex: 10,
                        touchAction: 'none',
                        backgroundImage: dragCol?.key === 'unitPrice' ? 'linear-gradient(to right, transparent 45%, #2020AF 45%, #2020AF 55%, transparent 55%)' : 'linear-gradient(to right, transparent 45%, #FF0000 45%, #FF0000 55%, transparent 55%)',
                        '&:hover': { backgroundColor: 'rgba(0,0,0,0.06)' }
                      }}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setDragCol({ key: 'unitPrice', startX: e.clientX, startW: columnWidths['unitPrice'] ?? defaultColumnWidths['unitPrice'] });
                      }}
                    />
                  )}
                </TableCell>
              )}
              {colV('value') && (
                <TableCell align="right" sx={{ cursor: groupView ? 'default' : 'pointer', color: '#2D2D32', width: getColWidthPx('value'), position: 'relative' }} onClick={()=> !groupView && handleSort('value')}>
                  {groupView ? 'Wartość' : <TableSortLabel {...getSortLabelProps(sortField==='value')} active={sortField==='value'} direction={sortField==='value' ? sortDir : 'asc'}>Wartość</TableSortLabel>}
                  {colResizeMode && (
                    <Box
                      role="separator"
                      aria-orientation="vertical"
                      sx={{
                        position: 'absolute',
                        right: 0,
                        top: 0,
                        bottom: 0,
                        width: '12px',
                        cursor: 'col-resize',
                        zIndex: 10,
                        touchAction: 'none',
                        backgroundImage: dragCol?.key === 'value' ? 'linear-gradient(to right, transparent 45%, #2020AF 45%, #2020AF 55%, transparent 55%)' : 'linear-gradient(to right, transparent 45%, #FF0000 45%, #FF0000 55%, transparent 55%)',
                        '&:hover': { backgroundColor: 'rgba(0,0,0,0.06)' }
                      }}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setDragCol({ key: 'value', startX: e.clientX, startW: columnWidths['value'] ?? defaultColumnWidths['value'] });
                      }}
                    />
                  )}
                </TableCell>
              )}
              {colV('actions') && (
                <TableCell align="center" sx={{ color: '#2D2D32', width: getColWidthPx('actions'), position: 'relative' }}>
                  {colResizeMode && (
                    <Box
                      role="separator"
                      aria-orientation="vertical"
                      sx={{
                        position: 'absolute',
                        right: 0,
                        top: 0,
                        bottom: 0,
                        width: '12px',
                        cursor: 'col-resize',
                        zIndex: 10,
                        touchAction: 'none',
                        backgroundImage: dragCol?.key === 'actions' ? 'linear-gradient(to right, transparent 45%, #2020AF 45%, #2020AF 55%, transparent 55%)' : 'linear-gradient(to right, transparent 45%, #FF0000 45%, #FF0000 55%, transparent 55%)',
                        '&:hover': { backgroundColor: 'rgba(0,0,0,0.06)' }
                      }}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setDragCol({ key: 'actions', startX: e.clientX, startW: columnWidths['actions'] ?? defaultColumnWidths['actions'] });
                      }}
                    />
                  )}
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {rowsWithHeaders.map(item => {
              if (item.type === 'header' && item.section) {
                const visibleColsCount = ALL_COLUMNS.filter(k => colV(k)).length;
                const hdrKey = item.section.id >= 0 ? `hdr-${item.section.id}` : 'hdr-unsectioned';
                return (
                  <TableRow key={hdrKey} sx={{ backgroundColor: '#e0e0e0' }}>
                    <TableCell colSpan={visibleColsCount} sx={{ fontWeight: 700 }}>
                      {item.section.id >= 0 ? `${(item.section.nr || '').replace(/\.+$/, '')}. ${item.section.name}` : item.section.name}
                    </TableCell>
                  </TableRow>
                );
              }
              const pos = item.pos!;
              const displayNr = (item as { displayNr?: string }).displayNr ?? pos.nr;
              return (
<React.Fragment key={pos.id}>
                <TableRow
                  tabIndex={0}
                  data-rowid={`pos-${pos.id}`}
                  selected={selectedRowId === `pos-${pos.id}` && !selectedCell}
                  onClick={() => { onSelectRow(`pos-${pos.id}`); setSelectedCell(null);} }
                  sx={ selectedRowId===`pos-${pos.id}` && !selectedCell ? { outline:'2px solid #1a73e8', outlineOffset:-2 } : {} }
                >
                  {colV('nr') && (
                  <TableCell
                    onClick={()=> pos.positionType==='detailed' && togglePos(pos.id)}
                    sx={{ cursor: pos.positionType==='detailed' ? 'pointer' : 'default', width: getColWidthPx('nr'), p:0, verticalAlign: 'middle' }}
                  >
                    <Box sx={{ display:'flex', alignItems:'center', pl:1, height: '100%' }}>
                      <Box component="span" sx={{ display:'flex', alignItems:'center', justifyContent:'center', width:'7px', height:'11px' }}>
                        {pos.positionType==='detailed' ? (
                          expanded.has(pos.id) ? (
                            <svg width="7" height="11" viewBox="0 0 7 11" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ transform: 'rotate(90deg)', display: 'block' }}>
                              <path d="M6.02125 5.34424C6.02125 5.46474 6.002 5.5769 5.9635 5.68074C5.925 5.78457 5.859 5.88332 5.7655 5.97699L1.27125 10.4712C1.13275 10.6096 0.958667 10.6804 0.749001 10.6837C0.539501 10.6869 0.36225 10.6161 0.21725 10.4712C0.0724167 10.3262 0 10.1506 0 9.94424C0 9.7379 0.0724167 9.56224 0.21725 9.41724L4.2905 5.34424L0.21725 1.27124C0.0789167 1.13274 0.00808339 0.958652 0.00475006 0.748986C0.00158339 0.539486 0.0724167 0.362235 0.21725 0.217236C0.36225 0.072402 0.537917 -1.43286e-05 0.74425 -1.43376e-05C0.950584 -1.43467e-05 1.12625 0.072402 1.27125 0.217236L5.7655 4.71149C5.859 4.80515 5.925 4.9039 5.9635 5.00774C6.002 5.11157 6.02125 5.22374 6.02125 5.34424Z" fill="#3F3FDC"/>
                            </svg>
                          ) : (
                            <svg width="7" height="11" viewBox="0 0 7 11" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: 'block' }}>
                              <path d="M6.02125 5.34424C6.02125 5.46474 6.002 5.5769 5.9635 5.68074C5.925 5.78457 5.859 5.88332 5.7655 5.97699L1.27125 10.4712C1.13275 10.6096 0.958667 10.6804 0.749001 10.6837C0.539501 10.6869 0.36225 10.6161 0.21725 10.4712C0.0724167 10.3262 0 10.1506 0 9.94424C0 9.7379 0.0724167 9.56224 0.21725 9.41724L4.2905 5.34424L0.21725 1.27124C0.0789167 1.13274 0.00808339 0.958652 0.00475006 0.748986C0.00158339 0.539486 0.0724167 0.362235 0.21725 0.217236C0.36225 0.072402 0.537917 -1.43286e-05 0.74425 -1.43376e-05C0.950584 -1.43467e-05 1.12625 0.072402 1.27125 0.217236L5.7655 4.71149C5.859 4.80515 5.925 4.9039 5.9635 5.00774C6.002 5.11157 6.02125 5.22374 6.02125 5.34424Z" fill="#3F3FDC"/>
                            </svg>
                          )
                        ) : ''}
                      </Box>
                      <Box component="span" sx={{ ml:1 }}>{displayNr}</Box>
                    </Box>
                  </TableCell>
                  )}
                  {colV('type') && (
                  <TableCell sx={{ width: getColWidthPx('type') }}>{pos.positionType === 'detailed' ? 'S' : 'U'}</TableCell>
                  )}
                  {colV('description') && (
                  <TableCell
                    sx={{
                      whiteSpace:'normal', wordBreak:'break-word',
                      width: getColWidthPx('description'),
                      ...(selectedCell?.posId===pos.id && selectedRowId?.startsWith('pos-') && selectedCell.field==='description' ? {
                        outline: `2px solid ${editingCell && editingCell.posId===pos.id && editingCell.field==='description' ? '#fb8c00' : '#1a73e8'}`,
                        outlineOffset:-2
                      } : {})
                    }}
                    onClick={()=>{onSelectRow(`pos-${pos.id}`); setSelectedCell({posId:pos.id, field:'description'});}}
                  >
                    {editingCell && editingCell.posId===pos.id && !editingCell.expenseId && editingCell.field==='description' ? (
                      <InputBase
                        value={editingCell.value}
                        onChange={e=>setEditingCell({...editingCell, value:e.target.value})}
                        autoFocus
                        sx={{ p:0, m:0, width:'100%', fontSize:'inherit', lineHeight:'inherit', height:'100%' }}
                        onKeyDown={(e)=> {
                          e.stopPropagation();
                          if(e.key==='Escape'){
                            setEditingCell(null);
                            setSelectedCell(null);
                          }
                          if(e.key==='Enter'){
                            onUpdatePosition({id:pos.id, field:'description', value: editingCell.value});
                            setEditingCell(null);
                          }
                        }}
                      />
                    ) : (
                      <>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0 }}>
                          {pos.basis && (() => {
                            const { base, attachment } = parseBasis(pos.basis);
                            return (
                              <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0, flexWrap: 'wrap' }}>
                                <Box component="span" sx={{
                                  fontFamily: 'Roboto, sans-serif',
                                  fontWeight: 500,
                                  fontSize: '14px',
                                  color: '#2D2D32'
                                }}>
                                  {base}
                                </Box>
                                {attachment && (
                                  <Chip 
                                    label={attachment}
                                    size="small"
                                    sx={{
                                      bgcolor: '#E6F5E6',
                                      color: '#020242',
                                      borderRadius: '4px',
                                      minHeight: 21,
                                      height: 21,
                                      ml: 0.5,
                                      '& .MuiChip-label': {
                                        px: 1,
                                        py: 0,
                                        fontFamily: 'Roboto, sans-serif',
                                        fontSize: '11px',
                                        fontWeight: 400,
                                        lineHeight: '21px'
                                      }
                                    }}
                                  />
                                )}
                              </Box>
                            );
                          })()}
                          {pos.description && (
                            <Box component="span" sx={{
                              display: 'block',
                              fontFamily: 'Roboto, sans-serif',
                              fontWeight: 400,
                              fontSize: '16px',
                              color: '#595966',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word'
                            }}>
                              {pos.description}
                            </Box>
                          )}
                        </Box>
                      </>
                    )}
                  </TableCell>
                  )}
                  {colV('quantity') && (
                  <TableCell
                    align="right"
                    sx={{ 
                      width: getColWidthPx('quantity'),
                      ...(selectedCell?.posId===pos.id && selectedRowId?.startsWith('pos-') && selectedCell.field==='quantity' ? { outline:`2px solid ${editingCell && editingCell.posId===pos.id && editingCell.field==='quantity' ? '#fb8c00' : '#1a73e8'}`, outlineOffset:-2 } : {})
                    }}
                    onClick={()=>{onSelectRow(`pos-${pos.id}`); setSelectedCell({posId:pos.id, field:'quantity'});}}
                  >
                    {editingCell && editingCell.posId===pos.id && !editingCell.expenseId && editingCell.field==='quantity' ? (
                      <InputBase
                        value={editingCell.value}
                        onChange={e=>setEditingCell({...editingCell, value:e.target.value})}
                        autoFocus
                        sx={{ p:0, m:0, width:'100%', textAlign:'right', fontSize:'inherit', lineHeight:'inherit', height:'100%' }}
                        onKeyDown={(e)=> {
                          e.stopPropagation();
                          if(e.key==='Escape'){
                            setEditingCell(null);
                            setSelectedCell(null);
                          }
                          if(e.key==='Enter'){
                            onUpdatePosition({id:pos.id, field:'quantity', value: parseFloat(editingCell.value)||0});
                            setEditingCell(null);
                          }
                        }}
                      />
                    ) : formatNumber(pos.quantity, precision.quantity)}
                  </TableCell>
                  )}
                  {colV('unit') && (
                  <TableCell
                    sx={{ 
                      width: getColWidthPx('unit'),
                      ...(selectedCell?.posId===pos.id && selectedRowId?.startsWith('pos-') && selectedCell.field==='unit' ? { outline:`2px solid ${editingCell && editingCell.posId===pos.id && editingCell.field==='unit' ? '#fb8c00' : '#1a73e8'}`, outlineOffset:-2 } : {})
                    }}
                    onClick={()=>{onSelectRow(`pos-${pos.id}`); setSelectedCell({posId:pos.id, field:'unit'});}}
                  >
                    {editingCell && editingCell.posId===pos.id && !editingCell.expenseId && editingCell.field==='unit' ? (
                      <InputBase
                        value={editingCell.value}
                        onChange={e=>setEditingCell({...editingCell, value:e.target.value})}
                        autoFocus
                        sx={{ p:0, m:0, width:'100%', fontSize:'inherit', lineHeight:'inherit', height:'100%' }}
                        onKeyDown={(e)=> {
                          e.stopPropagation();
                          if(e.key==='Escape'){
                            setEditingCell(null);
                            setSelectedCell(null);
                          }
                          if(e.key==='Enter'){
                            onUpdatePosition({id:pos.id, field:'unit', value: editingCell.value});
                            setEditingCell(null);
                          }
                        }}
                      />
                    ) : pos.unit}
                  </TableCell>
                  )}
                  {colV('norm') && (
                  <TableCell align="right" sx={{ width: getColWidthPx('norm') }}>–</TableCell>
                  )}
                  {colV('expenses') && (
                  <TableCell align="right" sx={{ width: getColWidthPx('expenses') }}>–</TableCell>
                  )}
                  {colV('rmsPrice') && (
                  <TableCell align="right" sx={{ width: getColWidthPx('rmsPrice') }}>–</TableCell>
                  )}
                  {colV('unitPrice') && (
                  <TableCell
                    align="right"
                    sx={{ 
                      width: getColWidthPx('unitPrice'),
                      ...(selectedCell?.posId===pos.id && selectedRowId?.startsWith('pos-') && selectedCell.field==='unitPrice' ? { outline:`2px solid ${editingCell && editingCell.posId===pos.id && editingCell.field==='unitPrice' ? '#fb8c00' : '#1a73e8'}`, outlineOffset:-2 } : {})
                    }}
                    onClick={()=>{ if(pos.positionType==='simplified'){ onSelectRow(`pos-${pos.id}`); setSelectedCell({posId:pos.id, field:'unitPrice'});} }}
                  >
                    {editingCell && editingCell.posId===pos.id && !editingCell.expenseId && editingCell.field==='unitPrice' ? (
                      <InputBase
                        value={editingCell.value}
                        onChange={e=>setEditingCell({...editingCell, value:e.target.value})}
                        autoFocus
                        sx={{ p:0, m:0, width:'100%', fontSize:'inherit', lineHeight:'inherit', height:'100%', textAlign:'right' }}
                        onKeyDown={(e)=> {
                          e.stopPropagation();
                          if(e.key==='Escape'){
                            setEditingCell(null);
                            setSelectedCell(null);
                          }
                          if(e.key==='Enter'){
                            const v = parseFloat(String(editingCell.value).replace(',', '.')) || 0;
                            onUpdatePosition({id:pos.id, field:'unitPrice', value: v});
                            setEditingCell(null);
                          }
                        }}
                      />
                    ) : (
                        pos.positionType==='simplified'
                          ? ((pos as any).unitPrice ? formatNumber((pos as any).unitPrice, precision.unitPrice) : '0,00')
                          : formatNumber(computeUnitPrice(pos), precision.unitPrice)
                      )}
                  </TableCell>
                  )}
                  {colV('value') && (
                  <TableCell align="right" sx={{ width: getColWidthPx('value') }}>{formatNumber(computeUnitPrice(pos)*pos.quantity, precision.unitPrice)}</TableCell>
                  )}
                  {colV('actions') && (
                  <TableCell align="center" sx={{ width: getColWidthPx('actions'), whiteSpace: 'nowrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                      <Tooltip title={positionStatuses[`pos-${pos.id}`] === 'approved' ? 'Zatwierdzone' : positionStatuses[`pos-${pos.id}`] === 'rejected' ? 'Odrzucone' : 'Sprawdzić'}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {getStatusIcon(positionStatuses[`pos-${pos.id}`] || 'check')}
                        </Box>
                      </Tooltip>
                      <IconButton size="small" onClick={(e)=>handleMenuOpen(e, `pos-${pos.id}`)} sx={{ padding: '4px' }}>
                        <MoreVert fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                  )}
                </TableRow>
                {expanded.has(pos.id) && (() => {
                  // obliczenia sum jednostkowych RMS dla danej pozycji
                  const childArr = pos.children ?? [];
                  const sumR = childArr.filter(c=>c.type==='R').reduce((s,c)=>s + c.norm*c.rmsPrice,0);
                  const sumM = childArr.filter(c=>c.type==='M').reduce((s,c)=>s + c.norm*c.rmsPrice,0);
                  const sumS = childArr.filter(c=>c.type==='S').reduce((s,c)=>s + c.norm*c.rmsPrice,0);

                  // wstępne ceny jedn. dla Kp i Kz
                  let kpUnit = 0, kzUnit = 0, zUnit = 0;
                  const kpPercentRow = childArr.find(c=>c.type==='Kp');
                  if(kpPercentRow) kpUnit = kpPercentRow.norm/100 * sumR;
                  const kzPercentRow = childArr.find(c=>c.type==='Kz');
                  if(kzPercentRow) kzUnit = kzPercentRow.norm/100 * sumM;
                  const zPercentRow = childArr.find(c=>c.type==='Z');
                  if(zPercentRow) zUnit = zPercentRow.norm/100 * (sumR+sumM+sumS+kpUnit+kzUnit);

                  return childArr.map(exp => {
                    const isRMS = ['R','M','S'].includes(exp.type);
                    const isPercent = ['Kp','Kz','Z'].includes(exp.type);
                    let unitPriceDisplay = '';
                    if(isRMS){
                      unitPriceDisplay = formatNumber(exp.norm * exp.rmsPrice, precision.rmsPrice);
                    } else if(exp.type==='Kp'){
                      unitPriceDisplay = formatNumber(kpUnit, precision.unitPrice);
                    } else if(exp.type==='Kz'){
                      unitPriceDisplay = formatNumber(kzUnit, precision.unitPrice);
                    } else if(exp.type==='Z'){
                      unitPriceDisplay = formatNumber(zUnit, precision.unitPrice);
                    }
                    return (
                    <TableRow
                      key={exp.id}
                      tabIndex={0}
                      data-rowid={`exp-${exp.id}`}
                      selected={selectedRowId === `exp-${exp.id}` && !selectedCell}
                      onClick={() => { onSelectRow(`exp-${exp.id}`); setSelectedCell(null);} }
                      sx={{
                        backgroundColor: isRMS ? '#fffde7' : isPercent ? '#fce4ec' : 'inherit',
                        outline: selectedRowId===`exp-${exp.id}` && !selectedCell ? '2px solid #1a73e8' : undefined,
                        outlineOffset: selectedRowId===`exp-${exp.id}` && !selectedCell ? -2 : undefined,
                        ...(isRMS || isPercent ? { '& .MuiTableCell-root': { fontSize: '0.72rem', py:'2px' } } : {})
                      }}
                    >
                      {colV('nr') && <TableCell sx={{ width: getColWidthPx('nr') }}></TableCell>}
                      {colV('type') && <TableCell sx={{ width: getColWidthPx('type') }}>{exp.type}</TableCell>}
                      {colV('description') && (
                        <TableCell
                        sx={{
                          width: getColWidthPx('description'),
                          ...((selectedCell?.expenseId===exp.id && selectedCell.field==='description')?{outline:'2px solid #1a73e8', outlineOffset:-2, whiteSpace:'normal', wordBreak:'break-word'}:{whiteSpace:'normal', wordBreak:'break-word'})
                        }}
                        onClick={()=>handleCellClick(pos.id, exp.id, 'description', exp.description)}
                       >
                        {editingCell && editingCell.expenseId===exp.id && editingCell.field==='description' ? (
                          <InputBase
                            value={editingCell.value}
                            onChange={e=>setEditingCell({...editingCell, value:e.target.value})}
                            autoFocus
                            sx={{ p:0, m:0, width:'100%', fontSize:'inherit', lineHeight:'inherit', height:'100%' }}
                            onKeyDown={(e)=> {
                              e.stopPropagation();
                              if(e.key==='Escape'){
                                setEditingCell(null);
                                setSelectedCell(null);
                              }
                              if(e.key==='Enter'){
                                const updatedChildren=(pos.children??[]).map(c=> c.id===exp.id ? {...c, description: editingCell.value}:c);
                                onUpdatePosition({id:pos.id, field:'children', value:updatedChildren});
                                setEditingCell(null);
                              }
                            }}
                          />
                        ) : exp.description}
                        </TableCell>
                      )}
                      {colV('quantity') && <TableCell align="right" sx={{ width: getColWidthPx('quantity') }}></TableCell>}
                      {colV('unit') && (
                        <TableCell
                        sx={{ 
                          width: getColWidthPx('unit'),
                          ...(selectedCell?.expenseId===exp.id && selectedCell.field==='unit' ? {outline:'2px solid #1a73e8', outlineOffset:-2} : {})
                        }}
                        onClick={()=>{ if(isRMS){ handleCellClick(pos.id, exp.id, 'unit', exp.unit); } }}
                      >
                        {editingCell && editingCell.expenseId===exp.id && editingCell.field==='unit' ? (
                          <InputBase
                            value={editingCell.value}
                            onChange={e=>setEditingCell({...editingCell, value:e.target.value})}
                            autoFocus
                            sx={{ p:0, m:0, width:'100%', fontSize:'inherit', lineHeight:'inherit', height:'100%' }}
                            onKeyDown={(e)=> {
                              e.stopPropagation();
                              if(e.key==='Escape'){
                                setEditingCell(null);
                                setSelectedCell(null);
                              }
                              if(e.key==='Enter'){
                                const updatedChildren=(pos.children??[]).map(c=> c.id===exp.id ? {...c, unit: editingCell.value}:c);
                                onUpdatePosition({id:pos.id, field:'children', value:updatedChildren});
                                setEditingCell(null);
                              }
                            }}
                          />
                        ) : (isPercent ? '%' : exp.unit)}
                        </TableCell>
                      )}
                      {colV('norm') && (
                        <TableCell
                        align="right"
                        sx={{ 
                          width: getColWidthPx('norm'),
                          ...(selectedCell?.expenseId===exp.id && selectedCell.field==='norm' ? {outline:'2px solid #1a73e8', outlineOffset:-2}: (editingRms?.expenseId===exp.id ? {outline:'2px solid #1a73e8', outlineOffset:-2}: {}))
                        }}
                        onClick={()=>{
                          if(isRMS){
                            // jeśli już zaznaczona – przejdź w tryb edycji
                            if(selectedCell?.expenseId===exp.id && selectedCell.field==='norm'){
                              setEditingRms({positionId:pos.id, expenseId:exp.id, value:exp.norm.toString()});
                            } else {
                              onSelectRow(`exp-${exp.id}`);
                              setSelectedCell({posId:pos.id, expenseId:exp.id, field:'norm'});
                            }
                          }
                        }}
                      >
                        {editingRms?.expenseId===exp.id ? (
                          <input
                            style={{width:'100%',textAlign:'right',fontSize:'inherit',border:'none',outline:'none'}}
                            value={editingRms.value}
                            autoFocus
                            onChange={e=> setEditingRms(editingRms ? {...editingRms, value:e.target.value}:null)}
                            onBlur={()=>{
                              if(!editingRms) return;
                              const newNorm=parseFloat(editingRms.value)||0;
                              const updatedChildren=(pos.children??[]).map(c=> c.id===editingRms.expenseId ? {...c, norm:newNorm, expenses:parseFloat((newNorm*pos.quantity).toFixed(3))}:c);
                              onUpdatePosition({id:pos.id, field:'children', value:updatedChildren});
                              setEditingRms(null);
                            }}
                            onKeyDown={e=>{
                              if(e.key==='Enter') (e.target as HTMLInputElement).blur();
                              if(e.key==='Escape') setEditingRms(null);
                            }}
                          />
                        ) : (isRMS ? formatNumber(exp.norm, precision.norm) : '')}
                        </TableCell>
                      )}
                      {colV('expenses') && (
                        <TableCell align="right" sx={{ width: getColWidthPx('expenses') }}>{isRMS ? formatNumber(exp.expenses, precision.expenses) : isPercent ? formatNumber(exp.norm, precision.unitPrice) : ''}</TableCell>
                      )}
                      {colV('rmsPrice') && (
                        <TableCell
                        align="right"
                        sx={{ 
                          width: getColWidthPx('rmsPrice'),
                          ...(selectedCell?.expenseId===exp.id && selectedCell.field==='rmsPrice' ? {outline:'2px solid #1a73e8', outlineOffset:-2} : {})
                        }}
                        onClick={()=>{ if(isRMS){ handleCellClick(pos.id, exp.id, 'rmsPrice', exp.rmsPrice); } }}
                      >
                        {editingCell && editingCell.expenseId===exp.id && editingCell.field==='rmsPrice' ? (
                          <InputBase
                            value={editingCell.value}
                            onChange={e=>setEditingCell({...editingCell, value:e.target.value})}
                            autoFocus
                            sx={{ p:0, m:0, width:'100%', fontSize:'inherit', lineHeight:'inherit', height:'100%', textAlign:'right' }}
                            onKeyDown={async (e)=> {
                              e.stopPropagation();
                              if(e.key==='Escape'){
                                setEditingCell(null);
                                setSelectedCell(null);
                              }
                              if(e.key==='Enter'){
                                const newPrice=parseFloat(editingCell.value)||0;
                                await updateRms(estimateId, exp.id, { rms_price: newPrice });
                                const updatedChildren=(pos.children??[]).map(c=> c.id===exp.id ? {...c, rmsPrice:newPrice}:c);
                                onUpdatePosition({id:pos.id, field:'children', value:updatedChildren});
                                setEditingCell(null);
                              }
                            }}
                          />
                        ) : (isRMS ? formatNumber(exp.rmsPrice, precision.rmsPrice) : '0,00')}
                        </TableCell>
                      )}
                      {colV('unitPrice') && (
                        <TableCell align="right" sx={{ width: getColWidthPx('unitPrice') }}>{unitPriceDisplay}</TableCell>
                      )}
                      {colV('value') && (
                        <TableCell align="right" sx={{ width: getColWidthPx('value') }}></TableCell>
                      )}
                      {colV('actions') && (
                        <TableCell align="center" sx={{ width: getColWidthPx('actions'), whiteSpace: 'nowrap' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                            {/* Niewidoczny placeholder dla wyrównania z ikoną statusu w wierszach głównych */}
                            <Box sx={{ width: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center' }}></Box>
                            <IconButton size="small" onClick={(e)=>handleMenuOpen(e, `exp-${exp.id}`)} sx={{ padding: '4px' }}>
                              <MoreVert fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                      )}
                    </TableRow>
                    )
                  })
                })()}
</React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      </Box>
      </Box>

      {/* Okno "Dostosuj kolumny" */}
      <Backdrop open={colsOpen} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.modal + 1, backgroundColor: 'rgba(0,0,0,0.35)', pointerEvents: 'none' }}>
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            bottom: 0,
            right: 0,
            height: '50vh',
            width: '280px',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0px 5px 30px rgba(0,0,0,0.15)',
            borderRadius: 0,
            zIndex: (theme) => theme.zIndex.modal + 2,
            backgroundColor: '#fff',
            pointerEvents: 'auto'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <Box
            sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flex: '0 0 auto', position: 'sticky', top: 0, bgcolor: '#FFFFFF', zIndex: 1 }}
          >
            <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: 16, color: '#2D2D32' }}>Dostosuj kolumny</Typography>
            <IconButton size="small" onClick={() => { 
              setColsAnchorEl(null); 
              setColResizeMode(false); 
              setDraftColumnsVisible(null); 
              setDraftWidths({});
              setDragCol(null);
            }}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
          <Divider />
          <Box sx={{ px: 0, py: 1.5, flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}>
            {(() => {
              const list = draftColumnsVisible ?? columnsVisible;
              const columnLabels: Record<string, string> = {
                nr: 'Nr',
                type: 'T',
                description: 'Podstawa, opis, elementy',
                quantity: 'Ilość',
                unit: 'Jmn',
                norm: 'Norma',
                expenses: 'Nakłady',
                rmsPrice: 'C.RMS',
                unitPrice: 'Cena jedn.',
                value: 'Wartość',
                actions: 'A'
              };
              return ALL_COLUMNS.map((key) => (
                <Box key={key} sx={{ py: 1, px: 2 }}>
                  <Box sx={{ border: '1px solid #d4d4d9', borderRadius: '4px', height: 36, width: 257, display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.25 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                      <IconButton onClick={() => setDraftColumnsVisible(prev => ({ ...(prev || {}), [key]: !((prev || list)[key] !== false) }))} size="small" sx={{ p: 0 }} aria-label="toggle column">
                        <Box component="img" src={(draftColumnsVisible ?? columnsVisible)[key] !== false ? '/assets/icons/checkbox-on.svg' : '/assets/icons/checkbox-off.svg'} alt="check" sx={{ width: 23, height: 24 }} />
                      </IconButton>
                      <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: 14, color: '#2D2D32' }}>{columnLabels[key] || key}</Typography>
                    </Box>
                    <Box
                      sx={{
                        width: '4px',
                        height: '20px',
                        backgroundColor: colResizeMode ? '#2020AF' : '#FF0000',
                        cursor: 'col-resize',
                        borderRadius: '2px',
                        flexShrink: 0
                      }}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setDragCol({ key, startX: e.clientX, startW: columnWidths[key] ?? defaultColumnWidths[key] });
                      }}
                    />
                  </Box>
                </Box>
              ));
            })()}
          </Box>
          <Divider />
          <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '24px', flex: '0 0 auto', boxShadow: '0px 4px 4px rgba(0,0,0,0.05)', position: 'sticky', bottom: 0, bgcolor: '#FFFFFF', zIndex: 1 }}>
            <Button variant="text" onClick={() => { 
              setColsAnchorEl(null); 
              setColResizeMode(false); 
              setDraftColumnsVisible(null); 
              setDraftWidths({});
              setDragCol(null);
            }} sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: 14, color: '#2D2D32', textTransform: 'none' }}>Anuluj</Button>
            <Button variant="contained" sx={{ width: 123, height: 40, borderRadius: '4px', textTransform: 'none', fontFamily: 'Roboto, sans-serif', fontWeight: 600, fontSize: 14, backgroundColor: '#020242', '&:hover': { backgroundColor: '#020242' } }} onClick={() => {
              if (draftColumnsVisible) {
                setColumnsVisible(draftColumnsVisible);
              }
              setColsAnchorEl(null);
              setColResizeMode(false);
              setDraftColumnsVisible(null);
              setDraftWidths({});
              setDragCol(null);
            }}>
              Zastosuj
            </Button>
          </Box>
        </Paper>
      </Backdrop>

      {/* Global menu dla akcji */}
      <Menu 
        anchorEl={menuAnchorEl} 
        open={menuOpen} 
        onClose={handleMenuClose} 
        anchorOrigin={{ vertical:'bottom', horizontal:'right' }} 
        transformOrigin={{ vertical:'top', horizontal:'right' }}
        MenuListProps={{
          'aria-labelledby': 'action-menu',
          autoFocus: false,
          onKeyDown: (e: React.KeyboardEvent) => {
            const menuItems = menuRowId && menuRowId.startsWith('exp-') 
              ? ['delete'] // Tylko "Usuń" dla wierszy składowych
              : ['edit', 'approved', 'check', 'rejected']; // Pełne menu dla pozycji głównych
            
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              e.stopPropagation();
              setSelectedMenuItemIndex(prev => {
                // Cykliczne przechodzenie: ostatnia pozycja -> pierwsza pozycja
                const newIndex = (prev + 1) % menuItems.length;
                // Ustaw focus na nowym elemencie
                setTimeout(() => {
                  const ref = menuItemRefs.current[newIndex];
                  if (ref) ref.focus();
                }, 0);
                return newIndex;
              });
              return;
            }
            if (e.key === 'ArrowUp') {
              e.preventDefault();
              e.stopPropagation();
              setSelectedMenuItemIndex(prev => {
                // Cykliczne przechodzenie: pierwsza pozycja -> ostatnia pozycja
                const newIndex = (prev - 1 + menuItems.length) % menuItems.length;
                // Ustaw focus na nowym elemencie
                setTimeout(() => {
                  const ref = menuItemRefs.current[newIndex];
                  if (ref) ref.focus();
                }, 0);
                return newIndex;
              });
              return;
            }
            if (e.key === 'Enter') {
              e.preventDefault();
              e.stopPropagation();
              const selectedAction = menuItems[selectedMenuItemIndex];
              if (selectedAction === 'delete') {
                handleDeleteRow();
              } else if (selectedAction === 'edit') {
                handleEditRow();
              } else if (selectedAction === 'approved' || selectedAction === 'check' || selectedAction === 'rejected') {
                handleStatusChange(selectedAction);
              }
              return;
            }
            // Zablokuj inne strzałki gdy menu jest otwarte
            if (["ArrowLeft", "ArrowRight"].includes(e.key)) {
              e.preventDefault();
              e.stopPropagation();
              return;
            }
          }
        }}
      >
        {menuRowId && menuRowId.startsWith('exp-') ? (
          // Menu dla wierszy składowych - tylko "Usuń"
          <MenuItem 
            ref={(el) => { menuItemRefs.current[0] = el; }}
            onClick={handleDeleteRow}
            selected={selectedMenuItemIndex === 0}
            onMouseEnter={() => setSelectedMenuItemIndex(0)}
          >
            <ListItemText>Usuń</ListItemText>
          </MenuItem>
        ) : (
          // Menu dla wierszy głównych - pełne opcje
          <>
            <MenuItem 
              ref={(el) => { 
                menuItemRefs.current[0] = el;
              }}
              onClick={handleEditRow}
              selected={selectedMenuItemIndex === 0}
              onMouseEnter={() => setSelectedMenuItemIndex(0)}
            >
              <ListItemIcon>
                <Box component="img" src="/assets/icons/edit2.svg" alt="Edytuj" sx={{ width: 18, height: 18 }} />
              </ListItemIcon>
              <ListItemText>Edytuj</ListItemText>
            </MenuItem>
            <MenuItem 
              ref={(el) => { menuItemRefs.current[1] = el; }}
              onClick={() => handleStatusChange('approved')}
              selected={selectedMenuItemIndex === 1}
              onMouseEnter={() => setSelectedMenuItemIndex(1)}
            >
              <ListItemIcon>
                <Box component="img" src="/assets/icons/zatwierdzam.svg" alt="Zatwierdzam" sx={{ width: 18, height: 18 }} />
              </ListItemIcon>
              <ListItemText>Status: zatwierdzam</ListItemText>
            </MenuItem>
            <MenuItem 
              ref={(el) => { menuItemRefs.current[2] = el; }}
              onClick={() => handleStatusChange('check')}
              selected={selectedMenuItemIndex === 2}
              onMouseEnter={() => setSelectedMenuItemIndex(2)}
            >
              <ListItemIcon>
                <Box component="img" src={encodeURI('/assets/icons/sprawdzić.svg')} alt="Sprawdzić" sx={{ width: 18, height: 18 }} />
              </ListItemIcon>
              <ListItemText>Status: sprawdzić</ListItemText>
            </MenuItem>
            <MenuItem 
              ref={(el) => { menuItemRefs.current[3] = el; }}
              onClick={() => handleStatusChange('rejected')}
              selected={selectedMenuItemIndex === 3}
              onMouseEnter={() => setSelectedMenuItemIndex(3)}
            >
              <ListItemIcon>
                <Box component="img" src="/assets/icons/odrzucam.svg" alt="Odrzucam" sx={{ width: 18, height: 18 }} />
              </ListItemIcon>
              <ListItemText>Status: odrzucam</ListItemText>
            </MenuItem>
          </>
        )}
        </Menu>

      <ExportToEasyEstimateDialog
        open={exportEeOpen}
        onClose={() => setExportEeOpen(false)}
        sourceEstimateId={estimateId}
        positions={positions as any}
        tesRows={tesRows}
        markups={markups}
      />
    </>
  );
};

export default DetailedEstimateSheet; 